import { observer } from "mobx-react";
import { useEffect, useMemo, useState } from "react";

import { InvisibleSmartCaptcha } from '@yandex/smart-captcha';

import ProjectsContainer from "../components/ProjectsContainer/ProjectsContainer";
import ProjectsGroupTitle from "../components/ProjectsGroup/ProjectsGroupTitle/ProjectsGroupTitle";
import ProjectsFilter from "../components/ProjectsFilter/ProjectsFilter";
import ProjectsArchiveRow from "./ProjectsArchiveRow/ProjectsArchiveRow";
import Preloader from "components/Preloader/Preloader";

import AppStore from "AppStore";
import messageBoxStore from "MessageBox/MessageBoxStore";

import { ApiError } from "client";

import { NO_PROJECTS_COMMENT_FOR_ARCHIVE } from "../../Projects/ProjectsConstants";

import {
//SMART_CAPTCHA_KEY,
  SMART_CAPTCHA_TEST,
} from '../../../smart-captcha.js';

import "./ProjectsArchive.scss";

const SMART_CAPTCHA_KEY = '';

const ProjectsArchive = observer(({ projects, isMobile }) => {
  const [expandedTypes, setExpandedTypes] = useState([]);
  const [openDropdown, setOpenDropdown] = useState(null);

  const groupedProjects = useMemo(() => {
    return projects?.reduce((acc, project) => {
      const type = project.type;
      if (!acc[type]) {
        acc[type] = [];
      }
      acc[type].push(project);
      return acc;
    }, {});
  }, [projects]);

  useEffect(() => {
    if (groupedProjects) {
      setExpandedTypes(Object.keys(groupedProjects || {}));
    }
  }, [groupedProjects]);

  const toggleType = (type) => {
    if (expandedTypes.includes(type)) {
      setExpandedTypes(expandedTypes.filter((t) => t !== type));
    } else {
      setExpandedTypes([...expandedTypes, type]);
    }
  };

  const toggleDropdown = (type) => {
    setOpenDropdown(openDropdown === type ? null : type);
  };

  const [captchaKey, setCaptchaKey] = useState(0);
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaVisible, setCaptchaVisible] = useState(false);

  const [projectIdToRepeat, setProjectIdToRepeat] = useState(null);

  const [isBusy, setIsBusy] = useState(false);

  async function onRepeatClick(project) {
    if (isBusy || captchaVisible)
      return;

    setProjectIdToRepeat(project.id);
    if (SMART_CAPTCHA_KEY)
      return setCaptchaVisible(true);
    onCaptchaSuccess(captchaToken, project.id);
  }

  async function onCaptchaSuccess(captchaToken, projectId) {
    if (!projectId)
      projectId = projectIdToRepeat;

    setCaptchaVisible(false);
    setCaptchaToken(captchaToken);

    setIsBusy(true);
    try {
      await AppStore.repeatProject(projectId, captchaToken);
    } catch (e) {
      setCaptchaKey(captchaKey + 1);
      if (e instanceof ApiError) {
        messageBoxStore.showError(e.message);
        return;
      }
      throw e;
    } finally {
      setIsBusy(false);
    }
  }

  // Архив грузится страницами, поэтому смена типа исполнителей перезапрашивает
  // его с сервера: до ответа показываем прелоадер, а не пустую плашку.
  if (AppStore.isLoadingMoreArchive && !projects?.length)
    return <div className="projects-archive"><Preloader /></div>;

  const loadedArchiveCount = AppStore.archiveProjects.length;

  return (
    <ProjectsContainer className="projects-archive" hasProjects={projects?.length > 0} emptyComment={NO_PROJECTS_COMMENT_FOR_ARCHIVE}>
      {Object.entries(groupedProjects).map(([type, typeProjects]) => {
        const filteredProjects = AppStore.getFilteredProjects('archive', type, typeProjects);
        const cities = AppStore.getCitiesForType(typeProjects);
        const shouldShowFilter = cities.length > 2;
        // Всего в группе знает только сводка: в списке лежит загруженный кусок
        // архива. При фильтре по городу общее число сравнивать не с чем.
        const typeTotal = filteredProjects.length === typeProjects.length
          ? AppStore.getArchiveTotalForType(type)
          : null;

        return (
          <div key={type}>
            <div className="projects-archive__row">
              <div className="projects-archive__row-header" onClick={() => toggleType(type)}>
                <ProjectsGroupTitle type={type} count={filteredProjects.length ? filteredProjects.length || typeProjects.length : 0} total={typeTotal} expandedTypes={expandedTypes.includes(type)} isMobile={isMobile} />
              </div>
              {shouldShowFilter &&
                <ProjectsFilter
                  cities={cities}
                  selectedCities={AppStore.selectedCities.archive}
                  type={type}
                  typeProjects={typeProjects}
                  onChange={(type, city) => AppStore.setSelectedCity('archive', type, city)}
                  isOpen={openDropdown === type}
                  onToggle={() => toggleDropdown(type)}
                  isMobile={isMobile}
                />
              }
            </div>

            <div className={`projects-archive__row-reports ${expandedTypes.includes(type) ? "expanded" : ""}`}>
              {filteredProjects.length > 0
                ? filteredProjects.map((project, i) =>
                  <ProjectsArchiveRow
                    key={i}
                    project={project}
                    isMobile={isMobile}
                    onRepeatClick={() => onRepeatClick(project)}
                  />
                )
                : <div className="projects-archive__no-results">Нет результатов</div>
              }
            </div>
          </div>
        );
      })}

      {AppStore.hasMoreArchive &&
        <div className="projects-archive__more">
          <button
            className="projects-archive__more-button"
            type="button"
            aria-label="Показать ещё проекты архива"
            disabled={AppStore.isLoadingMoreArchive}
            onClick={() => AppStore.loadMoreArchive()}
          >
            {AppStore.isLoadingMoreArchive ? "Загружаем…" : "Показать ещё"}
          </button>
          <p className="projects-archive__more-counter">
            Показано {loadedArchiveCount} из {AppStore.archiveTotal}
          </p>
        </div>
      }

      {SMART_CAPTCHA_KEY && <InvisibleSmartCaptcha
        key={captchaKey}
        sitekey={SMART_CAPTCHA_KEY}
        test={SMART_CAPTCHA_TEST}
        visible={captchaVisible}
        hideShield={true}
        onSuccess={onCaptchaSuccess}
        onTokenExpired={() => setCaptchaToken('')}
        onNetworkError={() => setCaptchaVisible(false)}
        onChallengeHidden={() => setCaptchaVisible(false)}
      />}

    </ProjectsContainer>
  );
})

export default ProjectsArchive;
